// Projective maps between quads: the math under mapped surfaces and the output corner pin. Matrices are row-major 3×3, applied to (x, y, 1).

const cross2 = (ax, ay, bx, by) => ax * by - ay * bx

/** The map from the unit square onto `corners` — (0,0)→q0, (1,0)→q1, (1,1)→q2, (0,1)→q3. A parallelogram yields an affine map. */
export const quadHomography = corners => {
	const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = corners
	const dx1 = x1 - x2
	const dx2 = x3 - x2
	const sx = x0 - x1 + x2 - x3
	const dy1 = y1 - y2
	const dy2 = y3 - y2
	const sy = y0 - y1 + y2 - y3
	const den = cross2(dx1, dy1, dx2, dy2)
	if (Math.abs(den) < 1e-9) return [x1 - x0, x3 - x0, x0, y1 - y0, y3 - y0, y0, 0, 0, 1]
	const g = cross2(sx, sy, dx2, dy2) / den
	const h = cross2(dx1, dy1, sx, sy) / den
	return [x1 - x0 + g * x1, x3 - x0 + h * x3, x0, y1 - y0 + g * y1, y3 - y0 + h * y3, y0, g, h, 1]
}

/** Inverse of a 3×3, or null when singular. */
export const invert3 = m => {
	const [a, b, c, d, e, f, g, h, i] = m
	const A = e * i - f * h
	const B = -(d * i - f * g)
	const C = d * h - e * g
	const det = a * A + b * B + c * C
	if (!Number.isFinite(det) || Math.abs(det) < 1e-12) return null
	return [
		A / det,
		-(b * i - c * h) / det,
		(b * f - c * e) / det,
		B / det,
		(a * i - c * g) / det,
		-(a * f - c * d) / det,
		C / det,
		-(a * h - b * g) / det,
		(a * e - b * d) / det,
	]
}

export const multiply3 = (a, b) => {
	const out = new Array(9)
	for (let r = 0; r < 3; r++)
		for (let c = 0; c < 3; c++) out[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c]
	return out
}

/** The map taking quad `from` onto quad `to`, normalised so the last entry is 1; null when either is degenerate. */
export const quadToQuad = (from, to) => {
	const inv = invert3(quadHomography(from))
	if (!inv) return null
	const m = multiply3(quadHomography(to), inv)
	const s = m[8]
	return Math.abs(s) < 1e-12 ? m : m.map(v => v / s)
}

export const applyHomography = (m, [x, y]) => {
	const w = m[6] * x + m[7] * y + m[8]
	return [(m[0] * x + m[1] * y + m[2]) / w, (m[3] * x + m[4] * y + m[5]) / w]
}

/** CSS `matrix3d()` for a homography. The element needs `transform-origin: 0 0`, so its local coordinates are the map's input. */
export const cssMatrix3d = m =>
	`matrix3d(${m[0]},${m[3]},0,${m[6]},${m[1]},${m[4]},0,${m[7]},0,0,1,0,${m[2]},${m[5]},0,${m[8]})`

/** The axis-aligned quad for a rectangle, in corner order. */
export const rectQuad = (x, y, w, h) => [
	[x, y],
	[x + w, y],
	[x + w, y + h],
	[x, y + h],
]
